'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import { getComponentProps } from '@wix/thunderbolt-elements/src/components/ConsentPolicyWrapper/viewer/utils';
import type {
  MapCameraChangedEvent,
  MapMouseEvent,
} from '@vis.gl/react-google-maps';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import type {
  GoogleMapProps,
  IGoogleMapsImperativeActions,
} from '../GoogleMap.types';
import MapContent from './GoogleMapContent';
import { createImperativeMethods } from './utils/ImperativeMethods';

import semanticClassNames from '../GoogleMap.semanticClassNames';
import { SKIN_BORDERS } from './skinParts';
import style from './style/GoogleMap.scss';
import { API_KEY, DEFAULT_ZOOM } from '../constants';

const GoogleMapReact: React.ForwardRefRenderFunction<
  IGoogleMapsImperativeActions,
  GoogleMapProps
> = (props, forwardRef) => {
  const {
    id,
    className,
    customClassNames = [],
    skin,
    mapData,
    translations,
    isConsentPolicyActive,
    onUpdateCenter,
    onUpdateZoom,
    onMarkerClicked,
    onMapClicked,
    onMouseEnter,
    onMouseLeave,
    language,
    onMapMount,
    lang,
  } = props;

  const SkinBorders = SKIN_BORDERS[skin];
  const mapRef = React.useRef<google.maps.Map | null>(null);

  // TODO update map state only with use interaction
  // or when relevant compData changes
  const [mapState, setMapState] = React.useState(() => ({
    center: {
      lat: mapData.locations[0]?.latitude ?? 0,
      lng: mapData.locations[0]?.longitude ?? 0,
    },
    zoom: mapData.zoom || DEFAULT_ZOOM,
  }));

  const updateMapState = React.useCallback(
    (newCenter: google.maps.LatLngLiteral, newZoom: number) => {
      setMapState(prevState => {
        if (
          prevState.center.lat !== newCenter.lat ||
          prevState.center.lng !== newCenter.lng ||
          prevState.zoom !== newZoom
        ) {
          return { center: newCenter, zoom: newZoom };
        }
        return prevState;
      });
    },
    [],
  );

  const handleCameraChanged = React.useCallback(
    (event: MapCameraChangedEvent) => {
      try {
        const center = event.detail.center;
        const zoom = event.detail.zoom;

        if (center && zoom !== undefined) {
          updateMapState(center, zoom);

          onUpdateCenter?.({ latitude: center.lat, longitude: center.lng });
          onUpdateZoom?.({ zoom });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('GoogleMap: Camera change handler error', {
          error: error instanceof Error ? error.message : String(error),
          componentId: id,
        });
      }
    },
    [updateMapState, onUpdateCenter, onUpdateZoom, id],
  );

  const handleMapClick = React.useCallback(
    (event: MapMouseEvent) => {
      try {
        if (onMapClicked && event.detail.latLng) {
          onMapClicked({
            type: 'mapClicked',
            location: {
              latitude: event.detail.latLng.lat,
              longitude: event.detail.latLng.lng,
            },
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('GoogleMap: Click handler error', {
          error: error instanceof Error ? error.message : String(error),
          componentId: id,
        });
      }
    },
    [onMapClicked, id],
  );

  const handleMapReady = React.useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  React.useImperativeHandle(
    forwardRef,
    () => createImperativeMethods(mapRef, updateMapState, mapData.locations),
    [updateMapState, mapData.locations],
  );

  React.useEffect(() => {
    onMapMount?.();
  }, [onMapMount]);

  const mapStyles = React.useMemo(() => {
    let styles;
    try {
      if (mapData.mapStyle) {
        if (typeof mapData.mapStyle === 'string') {
          styles = JSON.parse(mapData.mapStyle);
        } else {
          styles = mapData.mapStyle;
        }

        if (styles && !Array.isArray(styles)) {
          throw new Error('Map styles must be an array format');
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('GoogleMap: Error parsing map style', {
        error: error instanceof Error ? error.message : String(error),
        providedStyle:
          typeof mapData.mapStyle === 'string'
            ? mapData.mapStyle.substring(0, 100) + '...'
            : 'Object',
        componentId: id,
      });
    }
    return styles;
  }, [mapData.mapStyle, id]);

  // Memoize map props to prevent unnecessary re-renders
  const mapProps = React.useMemo(
    () => ({
      zoom: mapData.zoom,
      styles: mapStyles,
      mapTypeId: (mapData.mapType || 'ROADMAP').toLowerCase(),
      fullscreenControl: false,
      clickableIcons: true,
      keyboardShortcuts: true,
      disableDoubleClickZoom: false,
      disableDefaultUI: true,
      gestureHandling: mapData.mapInteractive ? 'auto' : 'none',
      mapTypeControl: mapData.showMapType,
      zoomControl: mapData.showZoom,
      streetViewControl: mapData.showStreetView,
    }),
    [
      mapData.zoom,
      mapStyles,
      mapData.mapType,
      mapData.mapInteractive,
      mapData.showMapType,
      mapData.showZoom,
      mapData.showStreetView,
    ],
  );

  const domSafeProps = {
    id,
    className: classNames(
      style[skin],
      className,
      formatClassNames(semanticClassNames.root, ...customClassNames),
    ),
    ...getDataAttributes(props),
    lang,
  };

  const baseComponentProps = getComponentProps(
    isConsentPolicyActive,
    domSafeProps,
  );

  return (
    <div
      {...baseComponentProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={style.mapContainer}
        aria-label={translations.title}
        role="region"
      >
        <APIProvider apiKey={API_KEY} language={language}>
          <Map
            {...mapProps}
            onCameraChanged={handleCameraChanged}
            onClick={handleMapClick}
            center={mapState.center}
            style={{ width: '100%', height: '100%' }}
          >
            <MapContent
              locations={mapData.locations}
              onMarkerClicked={onMarkerClicked}
              onMapReady={handleMapReady}
            />
          </Map>
        </APIProvider>
      </div>
      <SkinBorders />
    </div>
  );
};

export default React.forwardRef(GoogleMapReact);
